import { useEffect, useState, type FormEvent } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading03Icon, Tick02Icon } from '@hugeicons/core-free-icons'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp'
import { register, verify } from '@/lib/api/auth'
import { executeRecaptcha } from '@/lib/utils/recaptcha'
import { markOtpConfirmed } from '@/lib/utils/authState'
import { getAcquisitionContext } from '@/lib/utils/acquisitionContext'
import { track } from '@/lib/utils/track'
import {
  SystemCardShell,
  SystemCardHeading,
  SystemCardActions,
  SystemCardPrimary,
  SystemCardSecondary,
  SystemCardProgress,
  SystemCardFooter,
} from '@/components/shared/layout'

const API_URL = import.meta.env.VITE_PUBLIC_API_URL || 'http://localhost:3000'

type Step =
  | 'loading'
  | 'email'
  | 'code'
  | 'confirm'
  | 'success'
  | 'missing'
  | 'expired'
  | 'cancelled'
  | 'error'

const PERMISSIONS = [
  'Read analytics, funnels and heatmaps',
  'Add sites and check the tracking script',
  'Create and manage A/B tests',
  'See your plan and data-point balance',
]

const validEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

/**
 * MCP sign-in consent (backend services/mcp-oauth.js). Signed-out visitors sign
 * in or create an account right here with an email code, so the agent's
 * consent token never leaves the page; a new account starts on the free 25,000
 * data points when it approves.
 */
export default function McpConnect() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, token, setToken, logout } = useAuth()
  const mcpToken = searchParams.get('token') || ''

  const [step, setStep] = useState<Step>('loading')
  const [approving, setApproving] = useState(false)
  const [client, setClient] = useState('')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [isNewUser, setIsNewUser] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!mcpToken) {
      setStep('missing')
      return
    }
    fetch(`${API_URL}/api/mcp/poll/${mcpToken}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) {
          setStep('expired')
        } else {
          setClient(data.client || '')
          setStep(token ? 'confirm' : 'email')
        }
      })
      .catch(() => setStep('error'))
    // Only on load: signing in on this page moves to `confirm` itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mcpToken])

  const requestCode = async (address: string) => {
    const cleanEmail = address.trim().toLowerCase()
    setBusy(true)
    try {
      const recaptchaToken = await executeRecaptcha(
        import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY || '',
        'signup',
      )
      const result = await register(cleanEmail, cleanEmail.split('@')[0] || 'Humblytics user', recaptchaToken)
      if (!result) throw new Error('verification_code_request_failed')
      if ('authToken' in result) {
        setToken(result.authToken)
        setStep('confirm')
        return
      }
      setIsNewUser(result.isNew)
      if (result.isNew) {
        track('signup_initiated', null, {
          ...getAcquisitionContext(),
          auth_method: 'email',
          auth_flow: 'mcp_connect',
        })
      }
      setEmail(cleanEmail)
      setCode('')
      setStep('code')
    } catch {
      toast.error('Could not send a code', { description: 'Try again in a moment.' })
    } finally {
      setBusy(false)
    }
  }

  const submitEmail = (event: FormEvent) => {
    event.preventDefault()
    if (!validEmail(email)) {
      toast.error('Enter a valid email address')
      return
    }
    void requestCode(email)
  }

  const submitCode = async (value: string) => {
    if (busy || value.length !== 6) return
    setBusy(true)
    try {
      const result = await verify(email, value)
      if (!result) {
        setCode('')
        toast.error("That code didn't work", { description: 'Check the latest email and try again.' })
        return
      }
      track(result.isNew ? 'signup_completed' : 'signin_completed', result.authToken, {
        ...getAcquisitionContext(),
        auth_method: 'email',
        auth_flow: 'mcp_connect',
        is_new_user: result.isNew,
      })
      markOtpConfirmed()
      setIsNewUser(result.isNew)
      setToken(result.authToken)
      setStep('confirm')
    } catch {
      setCode('')
      toast.error('Verification failed', { description: 'Try again in a moment.' })
    } finally {
      setBusy(false)
    }
  }

  const handleApprove = async () => {
    setApproving(true)
    try {
      const res = await fetch(`${API_URL}/api/mcp/approve/${mcpToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
      })
      if (res.status === 401) {
        logout()
        setStep('email')
        return
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setStep(data.error === 'invalid_or_expired_token' ? 'expired' : 'error')
        return
      }
      const data = await res.json().catch(() => ({}))
      if (data.redirect) {
        window.location.replace(data.redirect)
        return
      }
      setStep('success')
    } catch {
      setStep('error')
    } finally {
      setApproving(false)
    }
  }

  const agent = client || 'your agent'
  const Agent = client || 'Your agent'
  const openDashboard = () => navigate('/dashboard')

  return (
    <SystemCardShell>
      {step === 'loading' && (
        <>
          <SystemCardHeading eyebrow="MCP connection" title="Checking the link" />
          <SystemCardProgress label="Verifying request" />
        </>
      )}

      {step === 'email' && (
        <form onSubmit={submitEmail} className="flex flex-col gap-7">
          <SystemCardHeading eyebrow="MCP connection" title={`Connect ${agent}`}>
            Sign in or create a free account.
          </SystemCardHeading>
          <div className="flex flex-col gap-2">
            <label htmlFor="mcp-email" className="text-[13px] font-medium leading-5">
              Email
            </label>
            <Input
              id="mcp-email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="h-11 rounded-lg bg-background px-3 text-sm shadow-none"
            />
          </div>
          <SystemCardActions>
            <SystemCardPrimary type="submit" disabled={busy}>
              {busy && <HugeiconsIcon icon={Loading03Icon} size={14} className="animate-spin" />}
              Continue
            </SystemCardPrimary>
          </SystemCardActions>
          <SystemCardFooter>New accounts start with 25,000 free data points. No card.</SystemCardFooter>
        </form>
      )}

      {step === 'code' && (
        <>
          <SystemCardHeading eyebrow="MCP connection" title="Check your email">
            Enter the code sent to <span className="font-medium text-foreground">{email}</span>.
          </SystemCardHeading>
          <InputOTP
            maxLength={6}
            pattern={REGEXP_ONLY_DIGITS}
            value={code}
            autoFocus
            disabled={busy}
            onChange={(value) => {
              setCode(value)
              if (value.length === 6) void submitCode(value)
            }}
            containerClassName="justify-center"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          <SystemCardActions>
            <SystemCardSecondary onClick={() => void requestCode(email)} disabled={busy}>
              Resend code
            </SystemCardSecondary>
            <SystemCardSecondary
              onClick={() => setStep('email')}
              disabled={busy}
              className="border-transparent bg-transparent"
            >
              Use a different email
            </SystemCardSecondary>
          </SystemCardActions>
        </>
      )}

      {step === 'confirm' && (
        <>
          <SystemCardHeading eyebrow="MCP connection" title={`Connect ${agent}`}>
            {Agent} is asking to use your Humblytics account
            {user?.email ? (
              <>
                , <span className="font-medium text-foreground">{user.email}</span>.
              </>
            ) : (
              '.'
            )}
          </SystemCardHeading>

          <div className="flex flex-col gap-2.5 rounded-lg bg-canvas px-4 py-3.5">
            <p className="text-[13px] font-medium leading-5 text-foreground">{Agent} will be able to</p>
            <ul className="flex flex-col gap-2.5">
              {PERMISSIONS.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[13px] leading-5 text-muted-foreground">
                  <span className="flex h-5 w-4 shrink-0 items-center justify-center">
                    <HugeiconsIcon icon={Tick02Icon} size={14} strokeWidth={2.2} className="text-foreground" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <SystemCardActions>
            <SystemCardPrimary onClick={handleApprove} disabled={approving}>
              {approving && <HugeiconsIcon icon={Loading03Icon} size={14} className="animate-spin" />}
              Allow access
            </SystemCardPrimary>
            <SystemCardSecondary onClick={() => setStep('cancelled')} disabled={approving}>
              Cancel
            </SystemCardSecondary>
          </SystemCardActions>

          <SystemCardFooter>
            {isNewUser ? 'Your free 25,000 data points start when you allow access. ' : ''}
            Allowing creates an API key for {agent}. Revoke it anytime on API &amp; MCP.
          </SystemCardFooter>
        </>
      )}

      {step === 'success' && (
        <>
          <SystemCardHeading success eyebrow="MCP connection" title={`Head back to ${agent}`}>
            {Agent} is connected to your Humblytics account. You can close this tab.
          </SystemCardHeading>
          <SystemCardSecondary onClick={openDashboard}>Open dashboard</SystemCardSecondary>
        </>
      )}

      {step === 'missing' && (
        <>
          <SystemCardHeading eyebrow="MCP connection" title="This link is incomplete">
            The connection link is missing its token. Ask your agent to start the connection again.
          </SystemCardHeading>
          <SystemCardSecondary onClick={openDashboard}>Open dashboard</SystemCardSecondary>
        </>
      )}

      {step === 'expired' && (
        <>
          <SystemCardHeading eyebrow="MCP connection" title="This link has expired">
            Ask your agent to start the connection again for a fresh link.
          </SystemCardHeading>
          <SystemCardSecondary onClick={openDashboard}>Open dashboard</SystemCardSecondary>
        </>
      )}

      {step === 'cancelled' && (
        <>
          <SystemCardHeading eyebrow="MCP connection" title={`${Agent} was not connected`}>
            No access was granted and no API key was created. You can close this tab.
          </SystemCardHeading>
          <SystemCardSecondary onClick={openDashboard}>Open dashboard</SystemCardSecondary>
        </>
      )}

      {step === 'error' && (
        <>
          <SystemCardHeading eyebrow="MCP connection" title="Couldn't reach Humblytics">
            Check your connection and try again.
          </SystemCardHeading>
          <SystemCardActions>
            <SystemCardPrimary onClick={() => window.location.reload()}>Try again</SystemCardPrimary>
            <SystemCardSecondary onClick={openDashboard}>Open dashboard</SystemCardSecondary>
          </SystemCardActions>
        </>
      )}
    </SystemCardShell>
  )
}
